import { apiClient, handleApiRequest } from "./client";
import type { DmCursor, DmUnreadCountResponse } from "@/types/direct-message";
import type {
  HelpConversationPairs,
  HelpInboxResponse,
  HelpMessage,
  HelpMessagePage,
  HelpPairMessage,
  HelpThread,
  HelpZettel,
  HelpZettelDetail,
  HelpZettelListResponse,
} from "@/types/help";

const AUDIO_EXT: Record<string, string> = {
  "audio/mp4": "mp4",
  "audio/m4a": "m4a",
  "audio/aac": "m4a",
  "audio/mpeg": "mp3",
  "audio/ogg": "ogg",
  "audio/wav": "wav",
  "audio/webm": "webm",
};

function audioFilename(blob: Blob): string {
  const mime = ((blob.type || "").split(";")[0] ?? "").trim().toLowerCase();
  return `talk.${AUDIO_EXT[mime] || "webm"}`;
}

export const helpApi = {
  getPushPublicKey: () =>
    handleApiRequest<{ public_key: string }>(
      apiClient.get("/api/v1/help/push/public-key")
    ),

  subscribePush: (endpoint: string, p256dh: string, auth: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post("/api/v1/help/push/subscribe", { endpoint, p256dh, auth })
    ),

  getUnreadCount: () =>
    handleApiRequest<DmUnreadCountResponse>(
      apiClient.get("/api/v1/help/unread-count")
    ),

  getMine: () => handleApiRequest<HelpThread>(apiClient.get("/api/v1/help/me")),

  getMyZettel: () =>
    handleApiRequest<HelpZettelListResponse>(
      apiClient.get("/api/v1/help/me/zettel")
    ),

  getInbox: (
    status: "open" | "done" = "open",
    q?: string,
    limit = 50,
    offset = 0
  ) =>
    handleApiRequest<HelpInboxResponse>(
      apiClient.get("/api/v1/help/inbox", {
        params: { status, q: q || undefined, limit, offset },
      })
    ),

  getThread: (conversationId: string) =>
    handleApiRequest<HelpThread>(
      apiClient.get(`/api/v1/help/${conversationId}`)
    ),

  getPairs: (
    conversationId: string,
    limit = 40,
    before?: string,
    beforeId?: string
  ) =>
    handleApiRequest<HelpConversationPairs>(
      apiClient.get(`/api/v1/help/${conversationId}/pairs`, {
        params: {
          limit,
          before: before || undefined,
          before_id: beforeId || undefined,
        },
      })
    ),

  getZettel: (conversationId: string, zettelId: string) =>
    handleApiRequest<HelpZettelDetail>(
      apiClient.get(`/api/v1/help/${conversationId}/zettel/${zettelId}`)
    ),

  getMessages: (conversationId: string, limit = 50, cursor?: DmCursor) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) {
      params.append("before", cursor.before);
      params.append("before_id", cursor.before_id);
    }
    return handleApiRequest<HelpMessagePage>(
      apiClient.get(`/api/v1/help/${conversationId}/messages?${params}`)
    );
  },

  sendMessage: (conversationId: string, body: string) =>
    handleApiRequest<HelpMessage>(
      apiClient.post(`/api/v1/help/${conversationId}/messages`, { body })
    ),

  speculate: (text: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.post("/api/v1/help/speculate", { text })
    ),

  sttTicket: () =>
    handleApiRequest<{ ticket: string }>(
      apiClient.post("/api/v1/help/stt-ticket")
    ),

  createZettel: (
    conversationId: string,
    body: string,
    clientId?: string,
    selectedZettelId?: string,
    wantTts = false
  ) =>
    handleApiRequest<{ zettel: HelpZettel; message: HelpPairMessage }>(
      apiClient.post(`/api/v1/help/${conversationId}/zettel`, {
        body,
        client_id: clientId,
        selected_zettel_id: selectedZettelId,
        want_tts: wantTts,
      })
    ),

  createZettelAudio: (
    conversationId: string,
    blob: Blob,
    clientId?: string,
    selectedZettelId?: string,
    durationMs?: number,
    wantTts = false,
    signal?: AbortSignal,
    transcript?: string
  ) => {
    const form = new FormData();
    form.append("audio", blob, audioFilename(blob));
    if (clientId) form.append("client_id", clientId);
    if (selectedZettelId) form.append("selected_zettel_id", selectedZettelId);
    if (durationMs != null && durationMs > 0)
      form.append("duration_ms", String(Math.round(durationMs)));
    const heard = (transcript || "").trim();
    if (heard) {
      form.append("transcript", heard);
      form.append("transcript_final", "true");
    }
    form.append("want_tts", wantTts ? "true" : "false");
    return handleApiRequest<{ zettel: HelpZettel; message: HelpPairMessage }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel-audio`,
        form,
        signal ? { signal } : undefined
      )
    );
  },

  applyTranscript: (conversationId: string, messageId: string, body: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/messages/${messageId}/transcript`,
        { body }
      )
    ),

  attachRecording: (
    conversationId: string,
    messageId: string,
    blob: Blob,
    durationMs?: number
  ) => {
    const form = new FormData();
    form.append("audio", blob, audioFilename(blob));
    if (durationMs != null && durationMs > 0)
      form.append("duration_ms", String(Math.round(durationMs)));
    return handleApiRequest<{ ok: boolean }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/messages/${messageId}/recording`,
        form
      )
    );
  },

  openLiveAudio: (conversationId: string) =>
    handleApiRequest<{ upload_id: string }>(
      apiClient.post(`/api/v1/help/${conversationId}/zettel-audio/live`)
    ),

  appendLiveAudio: async (
    conversationId: string,
    uploadId: string,
    blob: Blob
  ) => {
    const data = await blob.arrayBuffer();
    return handleApiRequest<{ ok: boolean; size: number }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel-audio/live/${uploadId}`,
        data,
        {
          headers: { "Content-Type": "application/octet-stream" },
          transformRequest: [(body) => body],
        }
      )
    );
  },

  finishLiveAudio: (
    conversationId: string,
    uploadId: string,
    clientId: string | undefined,
    selectedZettelId: string | undefined,
    durationMs: number | undefined,
    wantTts: boolean,
    mime: string,
    blob?: Blob
  ) => {
    const form = new FormData();
    if (blob) form.append("audio", blob, audioFilename(blob));
    if (clientId) form.append("client_id", clientId);
    if (selectedZettelId) form.append("selected_zettel_id", selectedZettelId);
    if (durationMs != null && durationMs > 0)
      form.append("duration_ms", String(Math.round(durationMs)));
    form.append("want_tts", wantTts ? "true" : "false");
    form.append("mime", blob?.type || mime || "audio/webm");
    return handleApiRequest<{ zettel: HelpZettel; message: HelpPairMessage }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel-audio/live/${uploadId}/finish`,
        form
      )
    );
  },

  abortLiveAudio: (conversationId: string, uploadId: string) =>
    handleApiRequest<{ ok: boolean }>(
      apiClient.delete(
        `/api/v1/help/${conversationId}/zettel-audio/live/${uploadId}`
      )
    ),

  setLanguage: (language: string) =>
    handleApiRequest<{ talk_language: string }>(
      apiClient.patch("/api/v1/help/me/language", { language })
    ),

  retryZettel: (conversationId: string, zettelId: string, wantTts?: boolean) =>
    handleApiRequest<{ zettel: HelpZettel }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/retry`,
        wantTts === undefined ? {} : { want_tts: wantTts }
      )
    ),

  replyZettel: (
    conversationId: string,
    zettelId: string,
    body: string,
    wantTts = false
  ) =>
    handleApiRequest<{ zettel: HelpZettel; message: HelpPairMessage }>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/reply`,
        { body, want_tts: wantTts }
      )
    ),

  supportReply: (conversationId: string, zettelId: string, body: string) =>
    handleApiRequest<HelpPairMessage>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/support-reply`,
        { body }
      )
    ),

  markZettelDone: (conversationId: string, zettelId: string) =>
    handleApiRequest<HelpZettel>(
      apiClient.post(`/api/v1/help/${conversationId}/zettel/${zettelId}/done`)
    ),

  deactivateZettel: (conversationId: string, zettelId: string) =>
    handleApiRequest<HelpZettel>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/deactivate`
      )
    ),

  confirmZettel: (conversationId: string, zettelId: string) =>
    handleApiRequest<HelpZettel>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/confirm`
      )
    ),

  discardZettel: (conversationId: string, zettelId: string) =>
    handleApiRequest<HelpZettel>(
      apiClient.post(
        `/api/v1/help/${conversationId}/zettel/${zettelId}/discard`
      )
    ),

  audioUrl: (conversationId: string, messageId: string) =>
    `/api/v1/help/${conversationId}/messages/${messageId}/audio`,

  getAudioBlob: async (
    conversationId: string,
    messageId: string,
    part?: string
  ) => {
    const res = await apiClient.get(
      `/api/v1/help/${conversationId}/messages/${messageId}/audio`,
      {
        responseType: "blob",
        headers: { Accept: "audio/*" },
        params: part ? { part } : undefined,
      }
    );
    return res.data as Blob;
  },

  markAsRead: (conversationId: string) =>
    handleApiRequest<{ message: string }>(
      apiClient.post(`/api/v1/help/${conversationId}/mark-read`)
    ),
};
